/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useRef, useContext } from 'react';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-extraneous-dependencies
import Grid from '@mui/material/Grid';
import Typography from '@digital-hig/mui/dist/Typography';
import Divider from '@mui/material/Divider';
import { triggerGenericPageView } from '../common/analyticsApi';
import {
  ENVIRONMENT, ITEM_TYPE_VIDEO, PAGETYPE_TUTORIAL, VERSION,
} from '../common/constants';
import { consumeObject } from '../common/userUtils';
import {
  // getLearningRecords,
  getUserDetails,
  getUserId,
  // hasFetchedLearningRecords,
  isAuthChecked,
} from '../store/userSlice';
import { UnitDetail } from '../pageTypes/unitDetail';
import Header from '../common/header';
import WTIH from './WTIH';
import RelatedLearning from './relatedLearning';
import EnvContext from '../common/envContext';
import DownloadableResources from './downloadableResources';

const TutorialBody = ({ tutorialData, showLoader }) => {
  const authChecked = useSelector(isAuthChecked);
  // const learningRecords = useSelector(getLearningRecords);
  const userDetails = useSelector(getUserDetails);
  const LRSConsumedFired = useRef();
  const hasVideoUnit = useRef();
  const unAuthAAFired = useRef(false);
  const pageViewCalled = useRef(false);
  // Ref used to identify the container holding the tutorial content
  const containerRef = useRef(false);
  // The div with this ref should always appear after all the tutorial content
  const { ref: endOfContentRef, inView: endOfContentInView } = useInView();
  const { unitLookup } = tutorialData;
  const envContext = useContext(EnvContext);
  const env = envContext[ENVIRONMENT];
  const appVersion = envContext[VERSION];
  const dispatch = useDispatch();
  const history = useHistory();
  const uid = useSelector(getUserId);

  const handleAnalyticsOnTutorialComplete = () => {
    triggerGenericPageView({
      tutorialData,
      eventName: 'tutorial-consumption',
    });
  };

  useEffect(() => {
    if (unitLookup && Object.keys(unitLookup)) {
      // Check if any content object in any of the units is a video
      hasVideoUnit.current = (Object.keys(unitLookup).filter(
        (unitId) => unitLookup[unitId]?.content?.filter(
          (item) => item?.type === ITEM_TYPE_VIDEO,
        ).length > 0,
      ).length > 0);
    }
  }, [tutorialData]);

  useEffect(() => {
    history.listen(() => {
      LRSConsumedFired.current = false;
      unAuthAAFired.current = false;
      pageViewCalled.current = false;
    });
  }, []);

  useEffect(() => {
    // If there is no video unit, the end of content div is in view, and it hasn't already been fired, fire the LRS Consumed event
    if (!hasVideoUnit?.current && endOfContentInView && !LRSConsumedFired?.current && userDetails?.userid) {
      consumeObject({
        dispatch, userDetails, env, appVersion, tutorialData,
      });
      LRSConsumedFired.current = true;
      handleAnalyticsOnTutorialComplete();
    }

    if (!hasVideoUnit?.current && endOfContentInView && authChecked && !uid && !unAuthAAFired.current) {
      handleAnalyticsOnTutorialComplete();
      unAuthAAFired.current = true;
    }
  }, [hasVideoUnit, endOfContentInView, uid, tutorialData, userDetails, authChecked]);

  useEffect(() => {
    // If there is a video, the container ref has been set, and the LRS Consumed hasn't already been fired, bind listeners to the videos
    // in the content container.
    if (hasVideoUnit?.current && containerRef && containerRef.current) {
      const videoObjects = containerRef.current.querySelectorAll('video');

      // Define function to trigger on video play
      // eslint-disable-next-line no-inner-declarations
      function playEvent() {
        if (!LRSConsumedFired?.current && userDetails?.userid) {
          // Remove all event listeners after first play of any video
          videoObjects.forEach((video) => {
            video.removeEventListener('play', playEvent);
          });

          consumeObject({
            dispatch, userDetails, env, appVersion, tutorialData,
          });

          // Set state to identify that LRS event has been fired
          LRSConsumedFired.current = true;
          handleAnalyticsOnTutorialComplete();
        }

        if (!uid && !unAuthAAFired.current) {
          handleAnalyticsOnTutorialComplete();
          unAuthAAFired.current = true;
        }
      }

      // Bind function to event listener on each video
      videoObjects.forEach((video) => {
        video.addEventListener('play', playEvent);
      });

      // Clean up any remaining event listeners when the component is destroyed
      return () => {
        videoObjects.forEach((video) => {
          video.removeEventListener('play', playEvent);
        });
      };
    }

    return () => {};
  }, [hasVideoUnit, containerRef, LRSConsumedFired, uid, tutorialData, userDetails]);

  return (
    <>
      {showLoader ? (
        <LoaderElement>
          <div className="loader">
            <UnitDetail showLoader />
            <Header showLoader />
          </div>

        </LoaderElement>
      ) : (
        <>
          { Object.keys(tutorialData).length > 0
            ? (
              <>
                <Header
                  pageType={PAGETYPE_TUTORIAL}
                  data={tutorialData}
                />
                <RootTElement className="maxWidth">
                  {tutorialData?.dataSets && tutorialData?.dataSets.length !== 0 && (
                    <>
                      <Grid container>
                        <Grid item xs={12}>
                          <Typography variant="headline-medium">Tutorial resources</Typography>
                          <Typography variant="body-copy-small">These downloadable resources will be used to complete this tutorial:</Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <DownloadableResources tutorialData={tutorialData} />
                        </Grid>
                      </Grid>
                      <Divider light orientation="horizontal" sx={{ marginTop: '30px', marginBottom: '48px' }} />

                    </>
                  )}
                  <div ref={containerRef}>
                    {tutorialData.units?.map((unit) => {
                      if (unitLookup && unitLookup[unit.id]) {
                        return <UnitDetail unitId={unit.id} unit={unitLookup[unit.id]} tutorialData={tutorialData} />;
                      }
                      return <></>;
                    })}
                  </div>
                  <div ref={endOfContentRef} className="end" />
                  <Grid container>
                    <Grid item xs={12}>
                      <WTIH
                        tutorialId={tutorialData.id}
                        tutorialType={tutorialData.isStandAlone ? 'standalone' : 'curated'}
                        tutorialName={tutorialData.title}
                        learningMode={tutorialData?.learningMode}
                      />
                    </Grid>
                  </Grid>
                </RootTElement>
                <RelatedLearningElement>
                  <Grid className="related-learning-container">
                    <Grid className="rl-container maxWidth">
                      <RelatedLearning
                        data={{
                          relatedLearning: tutorialData?.relatedLearning,
                          relatedTags: tutorialData?.relatedTags,
                        }}
                        optionsOverride={{ tabletCount: 3, desktopCount: 4 }}
                      />
                    </Grid>
                  </Grid>
                </RelatedLearningElement>
              </>
            ) : <></>}
        </>
      ) }

    </>
  );
};

const RootTElement = styled.div`
.MuiGrid-root.MuiGrid-item{
  padding-left: 0;
}
  margin: 54px auto !important;
  padding-top: 80px;
  padding-bottom: 80px;
  box-sizing: content-box;
  
  .loader {
    max-width: 1220px;
    margin: 40px auto;
  }
  div.end {
    height: 0px;
  }
  .unitItem:not(:last-child){
    margin-bottom: 58px;
  }
  `;

const RelatedLearningElement = styled.div`
  .related-learning-container {
    background-color: rgba(249, 249, 249, 1);  
    
    .rl-container{
      margin: 0px auto;
      padding-top: 80px;
      padding-bottom: 80px;
      box-sizing: content-box;
    }
  }
`;

const LoaderElement = styled.div`
      margin: 0px auto;
      padding-top: 80px;
      padding-bottom: 80px;
      box-sizing: content-box;
  
`;

TutorialBody.propTypes = {
  tutorialData: PropTypes.shape({
    id: PropTypes.string,
    uri: PropTypes.string,
    relatedLearning: PropTypes.arrayOf(),
    relatedTags: PropTypes.shape({}),
    title: PropTypes.string,
    isStandAlone: PropTypes.bool,
    units: PropTypes.arrayOf(PropTypes.shape({})),
    unitLookup: PropTypes.shape({}),
    dataSets: PropTypes.arrayOf(),
    learningMode: PropTypes.string,
  }).isRequired,
  showLoader: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
};

export default React.memo(TutorialBody);
